import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ProductMixAnalysis.module.scss";
import { Card, Table, Select, Typography, DatePicker, Spin, message, Tag, Skeleton } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ThunderboltOutlined,
  BlockOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import type { ProductMixAlert, ProductMixInsight } from "../../../types/productMixAnalysis";
import moment, { Moment } from "moment";
import {
  getProductMixAnalysis,
  getAllChannels,
  getAllBranchOffices,
  getCategoriesFiltered,
  getBrandsFiltered,
  queryArticlesByName,
} from "../../../api/Services";
import { DEFAULT_FILTER_DEBOUNCE_MS } from "../../../helpers/FilterSearchHelper";
import type {
  ProductMixAnalysis as ProductMixData,
  ProductMixBrandRow,
  ProductMixCategoryRow,
  ProductMixMixEvolutionRow,
  ProductMixPriceRangeRow,
  ProductMixUnitsVsSaleRow,
} from "../../../types/productMixAnalysis";

const { Title, Text } = Typography;

const fmt = (v: number) =>
  `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const Vr: React.FC<{ value: number; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const CategoryCell: React.FC<{ name: string; level: number | null }> = ({
  name,
  level,
}) => (
  <>
    {name}
    {level != null && (
      <Tag
        color={level === 1 ? "purple" : level === 2 ? "blue" : "default"}
        style={{ marginLeft: 8, fontSize: 10 }}
      >
        N{level}
      </Tag>
    )}
  </>
);

const TableRowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 6 }) => (
  <div className={styles.tableSkeleton}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} active title={false} paragraph={{ rows: 1, width: ["100%"] }} />
    ))}
  </div>
);

type PeriodKey = "month" | "90d";

const periodRange = (key: PeriodKey): [Moment, Moment] => {
  const today = moment();
  if (key === "month") {
    return [today.clone().startOf("month"), today.clone()];
  }
  return [today.clone().subtract(89, "days"), today.clone()];
};

const SectionCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <Card className={styles.sectionCard} bordered={false}>
    <div className={styles.sectionHeader}>
      <div>
        <Title level={5} className={styles.sectionTitle}>
          {title}
        </Title>
      </div>
      {icon && <span className={styles.sectionIcon}>{icon}</span>}
    </div>
    {children}
  </Card>
);

const ProductMixAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const [periodKey, setPeriodKey] = useState<PeriodKey>("month");
  const [dateStart, setDateStart] = useState(moment().startOf("month"));
  const [dateEnd, setDateEnd] = useState(moment());
  const [channelId, setChannelId] = useState<number | undefined>();
  const [branchOfficeId, setBranchOfficeId] = useState<number | undefined>();
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [brandId, setBrandId] = useState<number | undefined>();
  const [articleId, setArticleId] = useState<number | undefined>();

  const [channelOptions, setChannelOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [storeOptions, setStoreOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [brandOptions, setBrandOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [articleOptions, setArticleOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [searchingCategories, setSearchingCategories] = useState(false);
  const [searchingBrands, setSearchingBrands] = useState(false);
  const [searchingArticles, setSearchingArticles] = useState(false);
  const categorySearchTimer = useRef<number | null>(null);
  const brandSearchTimer = useRef<number | null>(null);
  const articleSearchTimer = useRef<number | null>(null);

  const [data, setData] = useState<ProductMixData | null>(null);
  const [loading, setLoading] = useState(false);

  const categoryDisplayName = useCallback(
    (row: { categoryId: number | null; categoryName: string }) =>
      row.categoryId == null || !row.categoryName
        ? t("ProductMixAnalysis-NoCategory")
        : row.categoryName,
    [t]
  );

  const brandDisplayName = useCallback(
    (row: { brandId: number | null; brandName: string }) =>
      row.brandId == null || !row.brandName
        ? t("ProductMixAnalysis-NoBrand")
        : row.brandName,
    [t]
  );

  const priceRangeLabel = useCallback(
    (row: ProductMixPriceRangeRow) => {
      if (row.rangeIndex != null && row.rangeIndex >= 0) {
        return t(`ProductMixAnalysis-PriceRange-${row.rangeIndex}`);
      }
      return row.rangeLabel || "";
    },
    [t]
  );

  const formatAnalysisMessage = useCallback(
    (key: string, params?: Record<string, string | number> | null) => {
      const resolved: Record<string, string | number> = { ...(params ?? {}) };
      if ("rangeIndex" in resolved) {
        resolved.rangeLabel = String(
          t(`ProductMixAnalysis-PriceRange-${resolved.rangeIndex}`)
        );
      }
      if ("name" in resolved && !resolved.name) {
        if (/Brand/i.test(key)) {
          resolved.name = String(t("ProductMixAnalysis-NoBrand"));
        } else if (/Category/i.test(key)) {
          resolved.name = String(t("ProductMixAnalysis-NoCategory"));
        }
      }
      return t(key, resolved);
    },
    [t]
  );

  const categoryLabel = useCallback((c: { name?: string; parents?: string[] }) => {
    const parents = Array.isArray(c.parents) ? c.parents.filter(Boolean) : [];
    return parents.length > 0 ? `${parents.join(" › ")} › ${c.name}` : c.name ?? "";
  }, []);

  const handleSearchCategories = useCallback(
    (value: string) => {
      if (categorySearchTimer.current != null) {
        window.clearTimeout(categorySearchTimer.current);
      }
      if (!value || value.trim().length < 2) {
        setCategoryOptions((prev) =>
          categoryId != null ? prev.filter((o) => o.value === categoryId) : []
        );
        setSearchingCategories(false);
        return;
      }
      setSearchingCategories(true);
      const selectedId = categoryId;
      categorySearchTimer.current = window.setTimeout(async () => {
        const response = await getCategoriesFiltered(value.trim());
        const mapped = (Array.isArray(response) ? response : []).map(
          (c: { id: number; name: string; parents?: string[] }) => ({
            value: c.id,
            label: categoryLabel(c),
          })
        );
        setCategoryOptions((prev) => {
          const selected = selectedId != null ? prev.find((o) => o.value === selectedId) : undefined;
          return selected && !mapped.some((o) => o.value === selected.value)
            ? [selected, ...mapped]
            : mapped;
        });
        setSearchingCategories(false);
      }, DEFAULT_FILTER_DEBOUNCE_MS);
    },
    [categoryId, categoryLabel]
  );

  const handleSearchBrands = useCallback(
    (value: string) => {
      if (brandSearchTimer.current != null) {
        window.clearTimeout(brandSearchTimer.current);
      }
      if (!value || value.trim().length < 2) {
        setBrandOptions((prev) =>
          brandId != null ? prev.filter((o) => o.value === brandId) : []
        );
        setSearchingBrands(false);
        return;
      }
      setSearchingBrands(true);
      const selectedId = brandId;
      brandSearchTimer.current = window.setTimeout(async () => {
        const response = await getBrandsFiltered(value.trim());
        const mapped = (Array.isArray(response) ? response : []).map(
          (b: { id: number; name: string }) => ({
            value: b.id,
            label: b.name,
          })
        );
        setBrandOptions((prev) => {
          const selected = selectedId != null ? prev.find((o) => o.value === selectedId) : undefined;
          return selected && !mapped.some((o) => o.value === selected.value)
            ? [selected, ...mapped]
            : mapped;
        });
        setSearchingBrands(false);
      }, DEFAULT_FILTER_DEBOUNCE_MS);
    },
    [brandId]
  );

  const handleSearchArticles = useCallback(
    (value: string) => {
      if (articleSearchTimer.current != null) {
        window.clearTimeout(articleSearchTimer.current);
      }
      if (!value || value.trim().length < 2) {
        setArticleOptions((prev) =>
          articleId != null ? prev.filter((o) => o.value === articleId) : []
        );
        setSearchingArticles(false);
        return;
      }
      setSearchingArticles(true);
      const selectedId = articleId;
      articleSearchTimer.current = window.setTimeout(async () => {
        const response = await queryArticlesByName(value.trim());
        const mapped = (Array.isArray(response) ? response : []).map(
          (a: { id: number; name: string }) => ({
            value: a.id,
            label: a.name,
          })
        );
        setArticleOptions((prev) => {
          const selected = selectedId != null ? prev.find((o) => o.value === selectedId) : undefined;
          return selected && !mapped.some((o) => o.value === selected.value)
            ? [selected, ...mapped]
            : mapped;
        });
        setSearchingArticles(false);
      }, DEFAULT_FILTER_DEBOUNCE_MS);
    },
    [articleId]
  );

  useEffect(() => {
    getAllChannels().then((channels: { id: number; type?: { type?: string } }[]) => {
      setChannelOptions(
        (channels ?? []).map((c) => ({
          value: c.id,
          label: c.type?.type ?? String(c.id),
        }))
      );
    });
    getAllBranchOffices().then((stores) => {
      setStoreOptions(
        (stores ?? []).map((b: { id: number; name: string }) => ({ value: b.id, label: b.name }))
      );
    });
    getCategoriesFiltered("").then((response) => {
      const mapped = (Array.isArray(response) ? response : []).map(
        (c: { id: number; name: string; parents?: string[] }) => ({
          value: c.id,
          label: categoryLabel(c),
        })
      );
      setCategoryOptions(mapped);
    });
    getBrandsFiltered("").then((response) => {
      const mapped = (Array.isArray(response) ? response : []).map(
        (b: { id: number; name: string }) => ({ value: b.id, label: b.name })
      );
      setBrandOptions(mapped);
    });
    queryArticlesByName("").then((response) => {
      const mapped = (Array.isArray(response) ? response : []).map(
        (a: { id: number; name: string }) => ({ value: a.id, label: a.name })
      );
      setArticleOptions(mapped);
    });
    return () => {
      if (categorySearchTimer.current != null) {
        window.clearTimeout(categorySearchTimer.current);
      }
      if (brandSearchTimer.current != null) {
        window.clearTimeout(brandSearchTimer.current);
      }
      if (articleSearchTimer.current != null) {
        window.clearTimeout(articleSearchTimer.current);
      }
    };
  }, [categoryLabel]);

  const loadData = useCallback(() => {
    setLoading(true);
    getProductMixAnalysis(
      dateStart.format("YYYY-MM-DD"),
      dateEnd.format("YYYY-MM-DD"),
      channelId,
      branchOfficeId,
      categoryId,
      brandId,
      articleId
    )
      .then((res) => setData(res as ProductMixData | null))
      .catch(() => {
        setData(null);
        message.error(t("ProductMixAnalysis-LoadError"));
      })
      .finally(() => setLoading(false));
  }, [dateStart, dateEnd, channelId, branchOfficeId, categoryId, brandId, articleId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePeriodPill = (key: PeriodKey) => {
    setPeriodKey(key);
    const [start, end] = periodRange(key);
    setDateStart(start);
    setDateEnd(end);
  };

  const hasData = data !== null;
  const kpis = data?.kpis;
  const categoryDist = data?.categoryDistribution ?? [];
  const brandDist = data?.brandDistribution ?? [];
  const alerts = data?.alerts ?? [];
  const insights = data?.insights ?? [];
  const priceRanges = data?.priceRanges ?? [];
  const mixEvolution = data?.mixEvolution ?? [];
  const unitsVsSales = data?.unitsVsSales ?? [];
  const portfolioTiers = data?.portfolioDependency ?? [];

  const top5 = portfolioTiers.find((tier) => tier.topN === 5);
  const top10 = portfolioTiers.find((tier) => tier.topN === 10);
  const top20 = portfolioTiers.find((tier) => tier.topN === 20);

  const catCols = useMemo(
    () => [
      {
        title: t("ProductMixAnalysis-Column-Category"),
        dataIndex: "categoryName",
        key: "categoryName",
        render: (_: string, row: ProductMixCategoryRow) => (
          <CategoryCell name={categoryDisplayName(row)} level={row.categoryLevel} />
        ),
      },
      {
        title: t("ProductMixAnalysis-Column-Sales"),
        dataIndex: "sales",
        key: "sales",
        align: "right" as const,
        render: (v: number) => <span className={styles.saleCell}>{fmt(v)}</span>,
      },
      {
        title: t("ProductMixAnalysis-Column-SalesPctTotal"),
        dataIndex: "salesPct",
        key: "salesPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-Margin"),
        dataIndex: "marginPct",
        key: "marginPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-VariationLy"),
        key: "var",
        align: "right" as const,
        render: (_: unknown, row: ProductMixCategoryRow) =>
          row.salesVariationLyPct != null ? (
            <Vr value={row.salesVariationLyPct} />
          ) : (
            "—"
          ),
      },
    ],
    [t, categoryDisplayName]
  );

  const brandCols = useMemo(
    () => [
      {
        title: t("ProductMixAnalysis-Column-Brand"),
        dataIndex: "brandName",
        key: "brandName",
        render: (_: string, row: ProductMixBrandRow) => brandDisplayName(row),
      },
      {
        title: t("ProductMixAnalysis-Column-Sales"),
        dataIndex: "sales",
        key: "sales",
        align: "right" as const,
        render: (v: number) => <span className={styles.saleCell}>{fmt(v)}</span>,
      },
      {
        title: t("ProductMixAnalysis-Column-SalesPctTotal"),
        dataIndex: "salesPct",
        key: "salesPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-Margin"),
        dataIndex: "marginPct",
        key: "marginPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-VariationLy"),
        key: "var",
        align: "right" as const,
        render: (_: unknown, row: ProductMixBrandRow) =>
          row.salesVariationLyPct != null ? (
            <Vr value={row.salesVariationLyPct} />
          ) : (
            "—"
          ),
      },
    ],
    [t, brandDisplayName]
  );

  const priceCols = useMemo(
    () => [
      {
        title: t("ProductMixAnalysis-Column-PriceRange"),
        dataIndex: "rangeLabel",
        key: "rangeLabel",
        render: (_: string, row: ProductMixPriceRangeRow) => priceRangeLabel(row),
      },
      {
        title: t("ProductMixAnalysis-Column-Sales"),
        dataIndex: "sales",
        key: "sales",
        align: "right" as const,
        render: (v: number) => <span className={styles.saleCell}>{fmt(v)}</span>,
      },
      {
        title: t("ProductMixAnalysis-Column-SalesPct"),
        dataIndex: "salesPct",
        key: "salesPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-Units"),
        dataIndex: "units",
        key: "units",
        align: "right" as const,
        render: (v: number) => fmtN(v),
      },
      {
        title: t("ProductMixAnalysis-Column-AvgTicket"),
        key: "tm",
        align: "right" as const,
        render: (_: unknown, row: ProductMixPriceRangeRow) =>
          row.avgTicket != null ? fmt(row.avgTicket) : "—",
      },
    ],
    [t, priceRangeLabel]
  );

  const evoCols = useMemo(
    () => [
      {
        title: t("ProductMixAnalysis-Column-Category"),
        dataIndex: "categoryName",
        key: "categoryName",
        render: (_: string, row: ProductMixMixEvolutionRow) => (
          <CategoryCell name={categoryDisplayName(row)} level={row.categoryLevel} />
        ),
      },
      {
        title: t("ProductMixAnalysis-Column-SalesPctCurrent"),
        dataIndex: "salesPctCurrent",
        key: "salesPctCurrent",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-SalesPctLy"),
        dataIndex: "salesPctLy",
        key: "salesPctLy",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-Variation"),
        key: "var",
        align: "right" as const,
        render: (_: unknown, row: ProductMixMixEvolutionRow) => (
          <Vr value={row.variationPp} suffix={t("ProductMixAnalysis-VariationPpSuffix")} />
        ),
      },
    ],
    [t, categoryDisplayName]
  );

  const uvCols = useMemo(
    () => [
      {
        title: t("ProductMixAnalysis-Column-Category"),
        dataIndex: "categoryName",
        key: "categoryName",
        render: (_: string, row: ProductMixUnitsVsSaleRow) => (
          <CategoryCell name={categoryDisplayName(row)} level={row.categoryLevel} />
        ),
      },
      {
        title: t("ProductMixAnalysis-Column-UnitsPct"),
        dataIndex: "unitsPct",
        key: "unitsPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-SalesPct"),
        dataIndex: "salesPct",
        key: "salesPct",
        align: "right" as const,
        render: (v: number) => `${v.toFixed(1)}%`,
      },
      {
        title: t("ProductMixAnalysis-Column-AvgTicket"),
        dataIndex: "avgTicket",
        key: "avgTicket",
        align: "right" as const,
        render: (v: number) => fmt(v),
      },
    ],
    [t, categoryDisplayName]
  );

  const periodPills = useMemo(
    (): { key: PeriodKey; label: string }[] => [
      { key: "month", label: t("ProductMixAnalysis-Period-Month") },
      { key: "90d", label: t("ProductMixAnalysis-Period-90d") },
    ],
    [t]
  );

  const portfolioItems = useMemo(
    () => [
      { label: t("ProductMixAnalysis-Portfolio-Top5"), tier: top5 },
      { label: t("ProductMixAnalysis-Portfolio-Top10"), tier: top10 },
      { label: t("ProductMixAnalysis-Portfolio-Top20"), tier: top20 },
    ],
    [t, top5, top10, top20]
  );

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("ProductMixAnalysis-PageTitle")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-DateStart")}
            </Text>
            <DatePicker
              className={styles.filterDate}
              format="DD/MM/YYYY"
              value={dateStart}
              onChange={(d) => d && setDateStart(d)}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-DateEnd")}
            </Text>
            <DatePicker
              className={styles.filterDate}
              format="DD/MM/YYYY"
              value={dateEnd}
              onChange={(d) => d && setDateEnd(d)}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-Channel")}
            </Text>
            <Select
              placeholder={t("ProductMixAnalysis-Filter-AllChannels")}
              allowClear
              value={channelId}
              onChange={setChannelId}
              className={styles.filterSelect}
              options={channelOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-Store")}
            </Text>
            <Select
              placeholder={t("ProductMixAnalysis-Filter-AllStores")}
              allowClear
              value={branchOfficeId}
              onChange={setBranchOfficeId}
              className={styles.filterSelect}
              options={storeOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-Category")}
            </Text>
            <Select
              placeholder={t("ProductMixAnalysis-Filter-AllCategories")}
              allowClear
              showSearch
              filterOption={false}
              defaultActiveFirstOption={false}
              value={categoryId}
              onChange={setCategoryId}
              onSearch={handleSearchCategories}
              notFoundContent={searchingCategories ? <Spin size="small" /> : null}
              className={styles.filterSelect}
              options={categoryOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-Brand")}
            </Text>
            <Select
              placeholder={t("ProductMixAnalysis-Filter-AllBrands")}
              allowClear
              showSearch
              filterOption={false}
              defaultActiveFirstOption={false}
              value={brandId}
              onChange={setBrandId}
              onSearch={handleSearchBrands}
              notFoundContent={searchingBrands ? <Spin size="small" /> : null}
              className={styles.filterSelect}
              options={brandOptions}
            />
          </div>
          <div className={styles.filterItem}>
            <Text className={styles.filterLabel}>
              {t("ProductMixAnalysis-Filter-Article")}
            </Text>
            <Select
              placeholder={t("ProductMixAnalysis-Filter-AllArticles")}
              allowClear
              showSearch
              filterOption={false}
              defaultActiveFirstOption={false}
              value={articleId}
              onChange={setArticleId}
              onSearch={handleSearchArticles}
              notFoundContent={searchingArticles ? <Spin size="small" /> : null}
              className={styles.filterSelect}
              options={articleOptions}
            />
          </div>
          <div className={styles.periodPills}>
            {periodPills.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`${styles.periodPill} ${
                  periodKey === p.key ? styles.active : ""
                }`}
                onClick={() => handlePeriodPill(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <div className={styles.kpiRow}>
        <Card className={styles.kpiCard} bordered={false}>
          <Text className={styles.kpiLabel}>
            {t("ProductMixAnalysis-Kpi-ArticlesSold")}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 70, height: 30 }} />
          ) : (
            <div className={styles.kpiValue}>{fmtN(kpis?.articlesSold ?? 0)}</div>
          )}
        </Card>
        <Card className={styles.kpiCard} bordered={false}>
          <Text className={styles.kpiLabel}>
            {t("ProductMixAnalysis-Kpi-CatalogTotal")}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 70, height: 30 }} />
          ) : (
            <div className={styles.kpiValue}>{fmtN(kpis?.catalogTotal ?? 0)}</div>
          )}
        </Card>
        <Card className={styles.kpiCard} bordered={false}>
          <Text className={styles.kpiLabel}>
            {t("ProductMixAnalysis-Kpi-CatalogActivePct")}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 70, height: 30 }} />
          ) : (
            <div className={styles.kpiValue}>
              {(kpis?.catalogActivePct ?? 0).toFixed(1)}%
            </div>
          )}
        </Card>
        <Card className={styles.kpiCard} bordered={false}>
          <Text className={styles.kpiLabel}>
            {t("ProductMixAnalysis-Kpi-AvgSale")}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 70, height: 30 }} />
          ) : (
            <div className={styles.kpiValue}>{fmt(kpis?.avgSalePerArticle ?? 0)}</div>
          )}
        </Card>
        <Card className={styles.kpiCard} bordered={false}>
          <Text className={styles.kpiLabel}>
            {t("ProductMixAnalysis-Kpi-AvgUnits")}
          </Text>
          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 70, height: 30 }} />
          ) : (
            <div className={styles.kpiValue}>
              {(kpis?.avgUnitsPerArticle ?? 0).toFixed(1)}
            </div>
          )}
        </Card>
      </div>

      <div className={styles.contentGrid}>
          <div className={styles.twoCol}>
            <SectionCard
              title={t("ProductMixAnalysis-Section-CategoryDist-Title")}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton />
              ) : (
                <Table
                  columns={catCols}
                  dataSource={categoryDist.map((r, i) => ({ ...r, key: i }))}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  size="small"
                  loading={loading && hasData}
                  className={styles.dataTable}
                />
              )}
            </SectionCard>

            <SectionCard
              title={t("ProductMixAnalysis-Section-Alerts-Title")}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton rows={3} />
              ) : alerts.length === 0 ? (
                <Text type="secondary" className={styles.emptySectionText}>
                  {t("ProductMixAnalysis-Alerts-Empty")}
                </Text>
              ) : (
                <div className={styles.alertList}>
                  {alerts.map((a: ProductMixAlert, i: number) => {
                    const variant =
                      a.variant === "danger"
                        ? "danger"
                        : a.variant === "info"
                          ? "info"
                          : "warning";
                    const Icon =
                      variant === "info" ? InfoCircleOutlined : WarningOutlined;
                    return (
                      <div
                        key={`${a.type}-${i}`}
                        className={`${styles.alertItem} ${styles[variant]}`}
                      >
                        <Icon className={styles.alertIcon} />
                        <div className={styles.alertBody}>
                          <Text className={styles.alertTitle}>
                            {formatAnalysisMessage(a.titleKey, a.params)}
                          </Text>
                          <Text className={styles.alertText}>
                            {formatAnalysisMessage(a.key, a.params)}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          </div>

          <div className={styles.twoColEqual}>
            <SectionCard
              title={t("ProductMixAnalysis-Section-BrandDist-Title")}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton />
              ) : (
                <Table
                  columns={brandCols}
                  dataSource={brandDist.map((r, i) => ({ ...r, key: i }))}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  size="small"
                  loading={loading && hasData}
                  className={styles.dataTable}
                />
              )}
            </SectionCard>

            <SectionCard
              title={t("ProductMixAnalysis-Section-PriceMix-Title")}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton rows={5} />
              ) : (
                <Table
                  columns={priceCols}
                  dataSource={priceRanges.map((r, i) => ({ ...r, key: i }))}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  size="small"
                  loading={loading && hasData}
                  className={styles.dataTable}
                />
              )}
            </SectionCard>
          </div>

          <div className={styles.twoColWide}>
            <SectionCard
              title={t("ProductMixAnalysis-Section-MixEvolution-Title")}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton />
              ) : (
                <Table
                  columns={evoCols}
                  dataSource={mixEvolution.map((r, i) => ({ ...r, key: i }))}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  size="small"
                  loading={loading && hasData}
                  className={styles.dataTable}
                />
              )}
            </SectionCard>

            <SectionCard
              title={t("ProductMixAnalysis-Section-Portfolio-Title")}
              icon={<BlockOutlined />}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton rows={3} />
              ) : (
                <div className={styles.depGrid}>
                  {portfolioItems.map((d) => (
                    <div className={styles.depItem} key={d.label}>
                      <Text className={styles.depLabel}>{d.label}</Text>
                      <div className={styles.depBarBg}>
                        <div
                          className={styles.depBarFill}
                          style={{ width: `${d.tier?.salesPct ?? 0}%` }}
                        />
                      </div>
                      <Text className={styles.depPct}>
                        {(d.tier?.salesPct ?? 0).toFixed(1)}%
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <div className={styles.twoColWide}>
            <SectionCard
              title={t("ProductMixAnalysis-Section-UnitsVsSales-Title")}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton />
              ) : (
                <Table
                  columns={uvCols}
                  dataSource={unitsVsSales.map((r, i) => ({ ...r, key: i }))}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  size="small"
                  loading={loading && hasData}
                  className={styles.dataTable}
                />
              )}
            </SectionCard>

            <SectionCard
              title={t("ProductMixAnalysis-Section-Insights-Title")}
              icon={<ThunderboltOutlined />}
            >
              {loading && !hasData ? (
                <TableRowsSkeleton rows={3} />
              ) : insights.length === 0 ? (
                <Text type="secondary" className={styles.emptySectionText}>
                  {t("ProductMixAnalysis-Insights-Empty")}
                </Text>
              ) : (
                <ul className={styles.insightList}>
                  {insights.map((insight: ProductMixInsight, i: number) => (
                    <li key={i} className={styles.insightItem}>
                      <span className={styles.insightBullet} />
                      <Text>
                        {formatAnalysisMessage(insight.key, insight.params)}
                      </Text>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
        </div>
    </div>
  );
};

export default ProductMixAnalysis;
